import { ConfirmReview } from "components/chat/DocumentReview/ConfirmReview";
import { ChatCardButton } from "components/ChatCard/ChatCardButton";
import {
  TASK_ADDITIONAL_PAYMENT_CARD_OPENED,
  TASK_AMENDMENT_COMPLETED_CARD_OPENED,
  TASK_REVIEW_AND_PAY_CARD_OPENED,
  TASK_REVIEW_DOCUMENT_CARD_OPENED,
} from "constants/analyticsEvents";
import {
  TASK_ADDITIONAL_PAYMENT,
  TASK_REVIEW_AND_PAY,
  TASK_REVIEW_DOCUMENT,
} from "constants/chatType";
import { TASK_AMENDMENT_DOCUMENTS } from "constants/taskStates";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { useToast } from "hooks/useToast";
import { useDispatch, useSelector } from "react-redux";
import { actionItemsApi } from "store/apis/actionItems";
import {
  useGetTaskFromChannelIdQuery,
  useMarkDocumentAsReviewMutation,
} from "store/apis/task";
import {
  closeReviewConfirmModal,
  openBalancingPaymentModal,
  openDocumentReviewModal,
  setAmount,
  setCustomData,
  setDocumentForReview,
  setDocumentForReviewMessageId,
  setDocumentOperationType,
  setDocumentReviewChannel,
  setHasReviewed,
  setIsDocReviewed,
  setIspaymentRequired,
} from "store/slices/reviewAndBalancePayment";
import { RootState } from "store/store";
import { useChannelStateContext } from "stream-chat-react";
import {
  CustomData,
  CustomDataFile,
} from "types/Models/reviewDocumentAndBalancingPayment";
import { CustomCardButton } from "./CustomCardButton";

type ReviewMessageChatCardProps = {
  customData: CustomData;
  messageId: string;
  completed?: boolean;
};

const cardTypes = {
  TASK_REVIEW_AND_PAY: "Review & Pay",
  TASK_ADDITIONAL_PAYMENT: "Pay",
  TASK_REVIEW_DOCUMENT: "Review",
};

export const CardTypeCTAS = ({
  customData,
  messageId,
  completed,
}: ReviewMessageChatCardProps) => {
  const dispatch = useDispatch();
  const { channelTaskId } = useSelector((state: RootState) => state.chat);
  const { channel } = useChannelStateContext();

  const { data: channelTask } = useGetTaskFromChannelIdQuery(
    {
      channelId: channel?.id!,
    },
    {
      skip: !channel?.id,
    }
  );

  const {
    activeChannelGroupId,
    documentForReviewMessageId,
    documentForReview,
    showReviewConfirmModal,
  } = useSelector((state: RootState) => state.reviewAndBalancePayment);
  const [documentMarkAsReview, { isLoading }] =
    useMarkDocumentAsReviewMutation();
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentChatGroupContext();
  const isPaymentRequired = customData.pending_payment?.payment_required;
  const isDocReviewed = customData.is_reviewed;
  const { trackEvent } = useAnalytics();

  const setAllData = () => {
    dispatch(setCustomData(customData));
    dispatch(setDocumentForReviewMessageId(messageId));
    dispatch(setIsDocReviewed(isDocReviewed));
    dispatch(setIspaymentRequired(isPaymentRequired));
    dispatch(setAmount(customData.pending_payment?.pending_amount));
  };

  const openReviewDocumentModal = (
    documentOperation: string,
    fileData: CustomDataFile[]
  ) => {
    setAllData();
    dispatch(setDocumentOperationType(documentOperation));
    dispatch(setDocumentForReview(fileData));
    dispatch(openDocumentReviewModal());
    dispatch(
      setDocumentReviewChannel({
        reviewChannelId: channel?.id!,
        reviewTaskId: channel?.data?.task_id as string,
      })
    );
  };

  const markDocumentAsReview = async () => {
    try {
      const documentIds = documentForReview
        .map(({ uuid }: { uuid: string }) => uuid)
        .join(",");
      await documentMarkAsReview({
        channel_id: channel?.id!,
        group_id: groupId || activeChannelGroupId,
        task_id: channelTaskId || (channel?.data?.task_id as string),
        message_id: documentForReviewMessageId,
        document_ids: documentIds,
      }).unwrap();
      successToast({ message: "Document marked as reviewed" });
    } catch (error: any) {
      alertToast({ message: error?.response?.data?.error?.message });
    }
  };

  const markAsReview = async () => {
    await markDocumentAsReview();
    dispatch(closeReviewConfirmModal());
  };

  const trackCardEvents = (documentOperation: string) => {
    switch (documentOperation) {
      case TASK_REVIEW_DOCUMENT:
        return trackEvent(TASK_REVIEW_DOCUMENT_CARD_OPENED);
      case TASK_REVIEW_AND_PAY:
        return trackEvent(TASK_REVIEW_AND_PAY_CARD_OPENED);
      case TASK_AMENDMENT_DOCUMENTS:
        return trackEvent(TASK_AMENDMENT_COMPLETED_CARD_OPENED);
      case TASK_ADDITIONAL_PAYMENT:
        return trackEvent(TASK_ADDITIONAL_PAYMENT_CARD_OPENED);

      default:
        break;
    }
  };

  const handleCardAction = (
    documentOperation: string,
    fileData: CustomDataFile[]
  ) => {
    trackCardEvents(documentOperation);
    if (isDocReviewed && isPaymentRequired && channelTask) {
      dispatch(setHasReviewed(documentOperation === TASK_REVIEW_AND_PAY));

      setAllData();
      if (channelTask?.entity?.uuid) {
        dispatch(openBalancingPaymentModal(channelTask?.entity?.uuid));
      }
    } else if (
      documentOperation === TASK_REVIEW_DOCUMENT ||
      documentOperation === TASK_REVIEW_AND_PAY ||
      documentOperation === TASK_AMENDMENT_DOCUMENTS
    ) {
      openReviewDocumentModal(documentOperation, fileData);
    }
  };

  return (
    <>
      {customData.ctas?.map(
        ({
          document_operation,
          metadata: { file_data },
          url_text,
        }: {
          document_operation: string;
          url_text: string;
          metadata: { file_data: CustomDataFile[] };
        }) => (
          <CustomCardButton
            key={document_operation}
            urlText={url_text}
            documentOperation={document_operation}
            isPaymentRequired={isPaymentRequired}
            isDocReviewed={isDocReviewed}
            disabled={!channelTask}
            handleCardAction={() =>
              handleCardAction(document_operation, file_data)
            }
            amount={customData.pending_payment?.pending_amount}
            customText={
              completed
                ? "View"
                : cardTypes[document_operation as keyof typeof cardTypes] ||
                  "Don't know"
            }
          />
        )
      )}
      {showReviewConfirmModal && (
        <ConfirmReview
          show={showReviewConfirmModal}
          closeModal={() => dispatch(closeReviewConfirmModal())}
          isLoading={isLoading}
          markDocumentAsReview={markAsReview}
        />
      )}
    </>
  );
};
